import { get, post, api } from '../api.js';
import { navigate } from '../router.js';
import {
  hasKeys,
  decryptItem,
  decryptItemRaw,
  wrapItemKeysForWill,
  encryptWillKeyForRecipient,
  encryptWillKeyWithLinkSecret,
  base64urlEncode,
} from '../crypto.js';
import { setTopbar, lucideIcons, esc } from '../app.js';
import { getConfig } from '../config.js';
import { relTime, parseDropEnvelope } from '../utils.js';
import { toast, confirmDialog } from '../dialog.js';

function itemDisplayName(item) {
  if (item.item_type === 'encrypted' && item.encrypted_blob) {
    try {
      const env = JSON.parse(decryptItem(item.encrypted_blob, item.wrapped_key, item.nonce, item.wrapped_key_format_version, item.encrypted_blob_format_version));
      if (env.name) return env.name;
    } catch {
      try {
        const rawBytes = decryptItemRaw(item.encrypted_blob, item.wrapped_key, item.nonce, item.wrapped_key_format_version, item.encrypted_blob_format_version);
        const { meta } = parseDropEnvelope(rawBytes);
        if (meta.name) return meta.name;
      } catch { /* fall through */ }
    }
  }
  return item.item_type || item.id;
}

function itemIsFile(item) {
  if (item.item_type === 'encrypted' && item.encrypted_blob) {
    try {
      const env = JSON.parse(decryptItem(item.encrypted_blob, item.wrapped_key, item.nonce, item.wrapped_key_format_version, item.encrypted_blob_format_version));
      return !!env.filename;
    } catch {
      try {
        const rawBytes = decryptItemRaw(item.encrypted_blob, item.wrapped_key, item.nonce, item.wrapped_key_format_version, item.encrypted_blob_format_version);
        const { meta } = parseDropEnvelope(rawBytes);
        return !!meta.name;
      } catch { return false; }
    }
  }
  return false;
}

export async function renderLegacy(container) {
  setTopbar('Legacy', '');
  container.innerHTML = '<p class="loading">Loading...</p>';

  try {
    let will = null;
    try { will = await get('/will'); } catch (_) {}

    let deadman = null;
    try { deadman = await get('/deadman'); } catch (_) {}

    let html = '';

    // ── Will Section ──
    html += '<div class="section-title">Will</div>';
    if (will) {
      const wrappedCount = Array.isArray(will.wrapped_items) ? will.wrapped_items.length : 0;
      html += `
        <div class="card" style="margin-bottom:24px">
          <div style="display:flex;gap:24px;flex-wrap:wrap">
            <div style="flex:1;min-width:200px">
              <div class="detail-label">Heir</div><div class="detail-value">${esc(will.heir_email)}</div>
              <div class="detail-label">Items Included</div><div class="detail-value">${wrappedCount}</div>
              <div class="detail-label">Grace Period</div><div class="detail-value">${will.grace_days} days</div>
            </div>
            <div style="flex:1;min-width:200px">
              <div class="detail-label">Last Updated</div><div class="detail-value">${new Date(will.updated_at).toLocaleString()}</div>
              <div class="detail-label">Encryption</div><div class="detail-value" style="display:flex;align-items:center;gap:6px;font-size:13px"><i data-lucide="lock" style="width:14px;height:14px;color:var(--success)"></i> Key-wrapped for heir</div>
            </div>
          </div>
          <div style="border-top:1px solid var(--border);margin-top:16px;padding-top:16px;display:flex;gap:8px">
            <button class="btn btn-secondary btn-sm" id="edit-will-btn"><i data-lucide="edit-3" style="width:14px;height:14px"></i> Edit Will</button>
            <button class="btn btn-danger btn-sm" id="delete-will-btn"><i data-lucide="trash-2" style="width:14px;height:14px"></i> Delete</button>
          </div>
        </div>`;
    } else {
      html += `
        <div class="card" style="margin-bottom:24px">
          <p style="color:var(--text-secondary);margin-bottom:12px">No will configured. Designate an heir who will receive access to selected vault items.</p>
          <button class="btn btn-primary btn-sm" style="width:auto" id="create-will-btn"><i data-lucide="plus" style="width:14px;height:14px"></i> Create Will</button>
        </div>`;
    }

    // ── Deadman Section ──
    html += '<div class="section-title">Dead Man\'s Switch</div>';
    if (deadman && deadman.enabled) {
      const lastCheckin = deadman.last_checkin_at ? relTime(deadman.last_checkin_at) : 'never';
      const nextDeadline = deadman.next_deadline_at ? relTime(deadman.next_deadline_at) : 'unknown';
      const limitedCheckin = !!deadman.limited_deadman_checkin_enabled;
      html += `
        <div class="card">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:20px">
            <div style="display:flex;align-items:center;gap:10px"><span class="status-dot active"></span><span style="font-weight:600">Active</span></div>
            <div class="toggle on" id="deadman-toggle"><div class="knob"></div></div>
          </div>
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:16px;margin-bottom:20px">
            <div><div class="detail-label">Interval</div><div class="detail-value">${deadman.interval_days} days</div></div>
            <div><div class="detail-label">Last Check-in</div><div class="detail-value">${lastCheckin}</div></div>
            <div><div class="detail-label">Next Deadline</div><div class="detail-value countdown" style="color:var(--warning)">${nextDeadline}</div></div>
          </div>
          <div id="deadman-msg"></div>
          <button class="btn btn-primary" id="checkin-btn"><i data-lucide="check-circle" style="width:16px;height:16px"></i> Check In Now</button>
          <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:24px;padding-top:20px;border-top:1px solid var(--border)">
            <div style="flex:1">
              <div style="font-weight:600;margin-bottom:4px">Allow check-in from email link alone</div>
              <div style="color:var(--text-secondary);font-size:13px">
                When on, you can check in from a magic-link session (no password needed). Trade-off: an attacker who controls your email could silently keep the switch alive.
              </div>
            </div>
            <div class="toggle ${limitedCheckin ? 'on' : ''}" id="deadman-limited-toggle"><div class="knob"></div></div>
          </div>
        </div>`;
    } else {
      html += `
        <div class="card">
          <p style="color:var(--text-secondary);margin-bottom:16px">If you don't check in within the configured interval, your heir (from your will) will automatically receive access to your vault.</p>
          <div id="deadman-msg"></div>
          <form id="deadman-form">
            <label>Check-in Interval (days) <input type="number" name="interval_days" value="${(getConfig() || {}).deadman_default_interval_days || 90}" min="${(getConfig() || {}).deadman_min_interval_days || 7}" required></label>
            <button type="submit" class="btn btn-primary" style="width:auto">Enable</button>
          </form>
        </div>`;
    }

    container.innerHTML = html;
    lucideIcons();

    // ── Wire up will buttons ──
    if (will) {
      document.getElementById('edit-will-btn').onclick = () => showWillForm(container, will);
      document.getElementById('delete-will-btn').onclick = async () => {
        if (!await confirmDialog('Delete your will? Your heir will no longer receive access.', { danger: true })) return;
        try {
          await api('DELETE', '/will');
          renderLegacy(container);
        } catch (err) { toast(err.message); }
      };
    } else {
      document.getElementById('create-will-btn').onclick = () => showWillForm(container, null);
    }

    // ── Wire up deadman buttons ──
    if (deadman && deadman.enabled) {
      document.getElementById('checkin-btn').onclick = async () => {
        const btn = document.getElementById('checkin-btn');
        const msg = document.getElementById('deadman-msg');
        try {
          await post('/deadman/checkin');
          btn.innerHTML = '<i data-lucide="check" style="width:16px;height:16px"></i> Checked In';
          btn.style.background = 'var(--success)';
          lucideIcons();
          msg.innerHTML = '<div class="msg msg-success">Check-in recorded</div>';
          setTimeout(() => renderLegacy(container), 1500);
        } catch (err) {
          msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        }
      };

      document.getElementById('deadman-toggle').onclick = async () => {
        if (!await confirmDialog('Disable dead man\'s switch?', { danger: true })) return;
        try {
          await api('DELETE', '/deadman');
          renderLegacy(container);
        } catch (err) { toast(err.message); }
      };

      const limitedToggle = document.getElementById('deadman-limited-toggle');
      if (limitedToggle) {
        limitedToggle.onclick = async () => {
          const turningOn = !limitedToggle.classList.contains('on');
          if (turningOn) {
            const confirmed = await confirmDialog(
              'Allow check-in from a magic-link-only session?\n\nAnyone who gains access to your email could silently keep the switch alive by clicking the login link. Only enable if the convenience is worth that trade-off.',
              { danger: true }
            );
            if (!confirmed) return;
          }
          try {
            await post('/deadman/limited-checkin', { enabled: turningOn });
            renderLegacy(container);
          } catch (err) { toast(err.message); }
        };
      }
    } else {
      const form = document.getElementById('deadman-form');
      if (form) {
        form.onsubmit = async (e) => {
          e.preventDefault();
          const msg = document.getElementById('deadman-msg');
          const btn = form.querySelector('button');
          const intervalDays = parseInt(form.interval_days.value);
          btn.disabled = true;
          msg.innerHTML = '';
          try {
            await post('/deadman', { interval_days: intervalDays });
            renderLegacy(container);
          } catch (err) {
            msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
            btn.disabled = false;
          }
        };
      }
    }
  } catch (err) {
    container.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
  }
}

async function showWillForm(container, existing) {
  setTopbar('Legacy', '');

  if (!hasKeys()) {
    container.innerHTML = '<div class="msg msg-error">Vault is locked. Please unlock first.</div>';
    return;
  }

  let items = [];
  try { items = await get('/items'); } catch (e) {
    container.innerHTML = `<div class="msg msg-error">Failed to load items: ${esc(e.message)}</div>`;
    return;
  }

  const existingItemIds = new Set();
  if (existing && Array.isArray(existing.wrapped_items)) {
    existing.wrapped_items.forEach(wi => existingItemIds.add(wi.item_id));
  }

  container.innerHTML = `
    <div class="breadcrumb" id="will-back"><i data-lucide="arrow-left" style="width:16px;height:16px"></i> Back to Legacy</div>
    <div class="card" style="max-width:600px">
      <h2 style="margin-bottom:8px">${existing ? 'Update' : 'Create'} Will</h2>
      <p style="color:var(--text-secondary);margin-bottom:1rem">
        Designate an heir who will receive access to selected vault items if your balance reaches zero or your dead man's switch triggers.
      </p>
      <div id="will-msg"></div>
      <form id="will-form">
        <div class="form-group"><label>Heir Email</label><input class="form-input" type="email" name="heir_email" value="${existing ? esc(existing.heir_email) : ''}" required></div>
        <div class="form-group"><label>Grace Days</label><input class="form-input" type="number" name="grace_days" value="${existing ? existing.grace_days : ((getConfig() || {}).will_default_grace_days || 30)}" min="1" required></div>
        <style>
          .item-select{position:relative;background:var(--bg-surface);border:1px solid var(--border);border-radius:var(--radius);padding:14px;cursor:pointer;transition:border-color .15s,box-shadow .15s,background .15s;user-select:none;display:flex;align-items:center;gap:12px}
          .item-select:hover{border-color:#2d3350;background:var(--bg-elevated)}
          .item-select.selected{border-color:var(--accent);box-shadow:0 0 0 1px var(--accent),0 0 20px rgba(62,207,180,.08)}
          .item-select input[type="checkbox"]{display:none}
          .check-indicator{width:20px;height:20px;min-width:20px;border-radius:6px;border:2px solid var(--border);display:flex;align-items:center;justify-content:center;transition:all .15s;color:transparent}
          .item-select.selected .check-indicator{background:var(--accent);border-color:var(--accent);color:var(--bg-base);animation:checkPop .2s ease-out}
          .will-item-info{flex:1;min-width:0}
          .will-item-info .name{font-weight:600;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
          .will-item-info .meta{font-size:11px;color:var(--text-secondary);margin-top:2px;display:flex;align-items:center;gap:6px}
          .select-all-btn{background:none;border:1px solid var(--border);border-radius:6px;color:var(--text-secondary);font-size:11px;font-weight:600;padding:4px 10px;cursor:pointer;font-family:inherit;text-transform:uppercase;letter-spacing:0.4px;transition:all .15s}
          .select-all-btn:hover{border-color:var(--accent);color:var(--accent)}
        </style>

        <div class="section-header" style="display:flex;align-items:center;justify-content:space-between;margin:24px 0 12px;padding-bottom:8px;border-bottom:1px solid var(--border)">
          <h3 style="font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:0.6px;color:var(--text-secondary);margin:0">Include in Will</h3>
          <div style="display:flex;align-items:center;gap:10px">
            <span id="sel-count" style="font-size:12px;color:var(--text-dim);font-weight:500;font-family:'JetBrains Mono',monospace"></span>
            <button type="button" class="select-all-btn" id="toggle-all">Select All</button>
          </div>
        </div>

        ${items.length === 0 ? '<p style="color:var(--text-secondary)">No items in vault yet.</p>' : `
        <div class="items-grid" id="will-items-grid">
          ${items.map(item => {
            const isFile = itemIsFile(item);
            const iconClass = isFile ? 'file' : 'encrypted';
            const iconName = isFile ? 'file-text' : 'lock';
            const badgeClass = isFile ? 'badge-drop' : 'badge-encrypted';
            const badgeText = isFile ? 'File' : 'Encrypted';
            const sel = existingItemIds.has(item.id);
            return `
            <label class="item-select${sel ? ' selected' : ''}" data-id="${item.id}">
              <input type="checkbox" name="item_ids" value="${item.id}" ${sel ? 'checked' : ''}>
              <div class="check-indicator"><i data-lucide="check" style="width:14px;height:14px"></i></div>
              <div class="item-icon ${iconClass}"><i data-lucide="${iconName}" style="width:18px;height:18px"></i></div>
              <div class="will-item-info">
                <div class="name">${esc(itemDisplayName(item))}</div>
                <div class="meta"><span class="badge ${badgeClass}">${badgeText}</span></div>
              </div>
            </label>`;
          }).join('')}
        </div>`}

        <div style="margin-top:16px"><button type="submit" class="btn btn-primary" style="width:auto">${existing ? 'Update' : 'Create'} Will</button></div>
      </form>
    </div>
  `;
  lucideIcons();

  // Selection interaction
  function updateCount() {
    const count = container.querySelectorAll('.item-select input:checked').length;
    const countEl = document.getElementById('sel-count');
    const toggleBtn = document.getElementById('toggle-all');
    if (countEl) countEl.textContent = `${count} of ${items.length} selected`;
    if (toggleBtn) toggleBtn.textContent = count === items.length ? 'Deselect All' : 'Select All';
  }

  container.querySelectorAll('.item-select').forEach(el => {
    el.addEventListener('click', () => {
      requestAnimationFrame(() => {
        const cb = el.querySelector('input[type="checkbox"]');
        el.classList.toggle('selected', cb.checked);
        updateCount();
      });
    });
  });

  const toggleBtn = document.getElementById('toggle-all');
  if (toggleBtn) {
    toggleBtn.addEventListener('click', () => {
      const allChecked = container.querySelectorAll('.item-select input:checked').length === items.length;
      container.querySelectorAll('.item-select').forEach(el => {
        const cb = el.querySelector('input[type="checkbox"]');
        cb.checked = !allChecked;
        el.classList.toggle('selected', !allChecked);
      });
      updateCount();
    });
  }

  updateCount();

  document.getElementById('will-back').onclick = () => renderLegacy(container);

  document.getElementById('will-form').onsubmit = async (e) => {
    e.preventDefault();
    const msg = document.getElementById('will-msg');
    const btn = e.target.querySelector('button[type=submit]');
    const heirEmail = e.target.heir_email.value;
    const graceDays = parseInt(e.target.grace_days.value);

    const checkboxes = e.target.querySelectorAll('input[name="item_ids"]:checked');
    const selectedIds = new Set(Array.from(checkboxes).map(cb => cb.value));
    const selectedItems = items.filter(it => selectedIds.has(it.id));

    if (selectedItems.length === 0) {
      msg.innerHTML = '<div class="msg msg-error">Select at least one item.</div>';
      return;
    }

    btn.disabled = true;
    msg.innerHTML = '<div class="msg">Encrypting will data...</div>';

    try {
      const willKey = new Uint8Array(32);
      crypto.getRandomValues(willKey);
      const wrappedItems = wrapItemKeysForWill(selectedItems, willKey);

      let encResult;
      let linkSecretUrl = null;
      try {
        const heirInfo = await get(`/users/public-key?email=${encodeURIComponent(heirEmail)}`);
        if (heirInfo && heirInfo.public_key && heirInfo.public_key.length > 1) {
          encResult = encryptWillKeyForRecipient(willKey, heirInfo.public_key);
        } else { throw new Error('no keys'); }
      } catch (_) {
        const lsResult = encryptWillKeyWithLinkSecret(willKey);
        encResult = { encrypted_will_key: lsResult.encrypted_will_key, ephemeral_pubkey: lsResult.ephemeral_pubkey };
        linkSecretUrl = base64urlEncode(lsResult.link_secret);
      }

      willKey.fill(0);

      const payload = {
        heir_email: heirEmail,
        encrypted_will_key: encResult.encrypted_will_key,
        ephemeral_pubkey: encResult.ephemeral_pubkey,
        wrapped_items: wrappedItems,
        grace_days: graceDays,
      };

      if (existing) { await api('PUT', '/will', payload); }
      else { await post('/will', payload); }

      if (linkSecretUrl) {
        msg.innerHTML = `
          <div class="msg msg-success">Will saved. Your heir is not registered, so you must share this secret link with them securely.</div>
          <div class="card" style="margin-top:1rem;word-break:break-all">
            <strong>Secret Link Fragment:</strong>
            <code style="display:block;margin-top:0.5rem;padding:0.5rem;background:var(--bg-elevated);border-radius:4px;font-size:0.85rem">#/will/TOKEN#${linkSecretUrl}</code>
            <p style="color:var(--text-secondary);margin-top:0.5rem;font-size:0.85rem">
              When the will activates, the heir will receive a link. They must append the secret to that URL. <strong>Save this secret.</strong>
            </p>
          </div>`;
      } else {
        msg.innerHTML = '<div class="msg msg-success">Will saved successfully. The will key is encrypted for your heir\'s account.</div>';
        setTimeout(() => renderLegacy(container), 2000);
      }
    } catch (err) {
      msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
      btn.disabled = false;
    }
  };
}

